import { forwardRef, type ButtonHTMLAttributes } from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils/cn";

// eslint-disable-next-line react-refresh/only-export-components
export const buttonVariants = cva(
  "inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-lg text-sm font-medium transition-all duration-150 select-none disabled:opacity-50 disabled:pointer-events-none",
  {
    variants: {
      variant: {
        primary:
          "bg-brand-grad text-white shadow-soft hover:shadow-glow hover:brightness-105 active:brightness-95",
        secondary: "bg-white text-ink border border-border hover:border-ink/30 hover:bg-bg",
        ghost: "bg-transparent text-ink hover:bg-bg",
        outline: "bg-transparent text-brand-700 border border-brand-200 hover:bg-brand-50",
        danger: "bg-danger text-white hover:bg-danger/90",
        dark: "bg-sidebar text-white hover:bg-sidebar/90",
        link: "bg-transparent text-brand-600 underline-offset-2 hover:underline",
      },
      size: {
        sm: "h-8 px-2.5 text-[12.5px]",
        md: "h-9 px-3.5 text-[13px]",
        lg: "h-10 px-4 text-[14px]",
        icon: "h-8 w-8",
      },
    },
    defaultVariants: { variant: "primary", size: "md" },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant, size, asChild = false, ...props },
  ref,
) {
  const Comp = asChild ? Slot : "button";
  return <Comp ref={ref} className={cn(buttonVariants({ variant, size, className }))} {...props} />;
});
